import {
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  ChangeEvent,
  FormEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import LoadingButton from "../../Buttons/LoadingButton/LoadingButton";
import DatePicker from "src/components/DatePicker";
import { VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material";
import { useGetRolesQuery } from "src/api/services/rolesApi";
import { genders } from "src/constants/genders";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { formatPhoneNumber, formatToE164 } from "src/utils/formatPhoneNumber";
import { format } from "date-fns";
import { reversedStatesMap, statesMap } from "src/constants/usStates";
import { useCreateEmployeeMutation } from "src/api/services/employeeApi";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

function isError(error: unknown): error is Error {
  return error != null && typeof error === "object" && "message" in error;
}

type NewMemberModalTypes = {
  open: boolean;
  onClose: (o: boolean) => void;
};

const NewMemberModal = ({ open, onClose }: NewMemberModalTypes) => {
  const [error, setError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [gender, setGender] = useState("");
  const [preferredName, setPreferredName] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [npiKey, setNpiKey] = useState<string>("");
  const [createEmployee, { isLoading }] = useCreateEmployeeMutation();
  const [assignedRoles, setAssignedRoles] = useState<string[]>([]);
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>("");
  const [region, setRegion] = useState("");

  const states = useMemo(() => {
    return Object.values(statesMap);
  }, []);

  const { data = [] } = useGetRolesQuery();

  const roles = useMemo(() => {
    return data.filter((r) => r.name != "Patient");
  }, [data]);

  const handleChange = (event: SelectChangeEvent<typeof assignedRoles>) => {
    const {
      target: { value },
    } = event;
    setAssignedRoles(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleRegion = useCallback(
    (_: SyntheticEvent, value: string | null) => {
      setRegion(value ?? "");
    },
    [setRegion]
  );

  const requireNpi = useMemo(() => {
    return assignedRoles.some((r) => r.includes("Provider"));
  }, [assignedRoles]);

  const handleFirstName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value),
    []
  );

  const handleLastName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value),
    []
  );

  const handleGender = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setGender(event.target.value);
  }, []);

  const handlePreferredName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPreferredName(event.target.value);
    },
    []
  );

  const handleNpiKey = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNpiKey(event.target.value);
  }, []);

  const handleBirthday = useCallback((value: PickerValue) => {
    setDateOfBirth(value);
  }, []);

  const handlePhoneNumber = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(formatPhoneNumber(event.target.value));
    },
    []
  );

  const handleSignUpNewMember = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email || !password) {
        setError("Please enter both email and password");
        return;
      }

      if (requireNpi && npiKey.length !== 10) {
        setError("Please enter valid NPI key");
        return;
      }

      try {
        await createEmployee({
          user: { email, password },
          updates: {
            npi_key: npiKey,
            region: reversedStatesMap[region],
            gender,
            preferred_name: preferredName,
            dob:
              dateOfBirth != null
                ? format(new Date(dateOfBirth), "yyyy-MM-dd")
                : undefined,
            phone_number:
              phoneNumber != null ? formatToE164(phoneNumber) : undefined,
            email: email,
            first_name: firstName,
            last_name: lastName,
            roles: roles
              .filter((r) => assignedRoles.includes(r.name))
              .map((r) => r.id),
          },
        }).unwrap();

        onClose(false);
        setEmail("");
        setPassword("");
        setFirstName("");
        setLastName("");
        setAssignedRoles([]);
        setPhoneNumber(null);
        setDateOfBirth(null);
        setPreferredName(null);
        setGender("");
        setNpiKey("");
        setRegion("");
      } catch (error: unknown) {
        setError(isError(error) ? error.message : "Something went wrong");
        return;
      }
    },
    [
      email,
      password,
      requireNpi,
      npiKey,
      createEmployee,
      region,
      gender,
      preferredName,
      dateOfBirth,
      phoneNumber,
      firstName,
      lastName,
      roles,
      onClose,
      assignedRoles,
    ]
  );

  return (
    <Modal open={open}>
      <Stack sx={{ ...desktopSx, minWidth: 700 }} gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Add a new member
          </Typography>
          <IconButton onClick={() => onClose(false)}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack
          gap={2}
          component="form"
          onSubmit={handleSignUpNewMember}
          autoComplete="off"
        >
          <Stack direction="row" gap={2}>
            <TextField
              fullWidth
              required
              label="Member's First Name"
              value={firstName}
              onChange={handleFirstName}
            />

            <TextField
              fullWidth
              required
              label="Member's Last Name"
              value={lastName}
              onChange={handleLastName}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <TextField
              fullWidth
              required
              type="email"
              label="Member's Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              fullWidth
              required
              type={!showPassword ? "password" : "text"}
              label="Member's Temporary Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((o) => !o)}
                        onMouseDown={() => setShowPassword((o) => !o)}
                      >
                        {showPassword ? (
                          <VisibilityRounded />
                        ) : (
                          <VisibilityOffRounded />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <FormControl fullWidth>
              <DatePicker
                value={dateOfBirth}
                onChange={handleBirthday}
                required
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                required
                select
                label="Gender"
                value={gender}
                onChange={handleGender}
              >
                {genders.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Stack>

          <Stack direction="row" gap={2}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="Preferred name"
                value={preferredName ?? ""}
                onChange={handlePreferredName}
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                onChange={handleRegion}
                value={region}
                fullWidth
                disablePortal
                options={states}
                renderInput={(params) => (
                  <TextField {...params} label="Region" required />
                )}
              />
            </FormControl>
          </Stack>

          <Stack direction="row" gap={2}>
            <FormControl fullWidth>
              <InputLabel id="multiple-checkbox-label">Roles</InputLabel>
              <Select
                labelId="multiple-checkbox-label"
                id="multiple-checkbox"
                multiple
                required
                value={assignedRoles}
                onChange={handleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    <Checkbox checked={assignedRoles.includes(role.name)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <TextField
                required
                label="Phone number"
                value={phoneNumber}
                onChange={handlePhoneNumber}
              />
            </FormControl>
          </Stack>

          <Collapse in={requireNpi} unmountOnExit sx={{ width: "49%" }}>
            <TextField
              label="NPI"
              required
              fullWidth
              onChange={handleNpiKey}
              value={npiKey}
            />
          </Collapse>

          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error.toString()}
            </Typography>
          ) : null}
          <LoadingButton
            loading={isLoading}
            sx={{ marginLeft: "auto" }}
            startIcon={<AddIcon />}
            type="submit"
          >
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default NewMemberModal;
